/*
  Behaviour for the Snusify product card. Two small custom elements, both
  following the theme's own conventions: extend BaseElement so listeners are
  torn down by its AbortController, bind with `this.on`, and use the Motion
  helper that already ships in vendor.js rather than a second observer library.

  Split out of snusify-products.js so the card works wherever it is rendered.
  Because more than one section on a page can load this file, both defines are
  guarded the way the theme guards its own shared assets (search.js,
  collection.js, cart.js): a classic script included twice executes twice, and
  a second customElements.define for the same tag throws.

  Loaded by snippets/snusify-product-card-assets.liquid.
*/

if (!customElements.get('snusify-meter')) {
  customElements.define(
    'snusify-meter',
    class SnusifyMeter extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        // Dots fill left to right the first time the card is in view, and stay
        // lit: re-running the fill on every scroll past read as a glitch. The
        // stagger is CSS transition-delay; this only flips the class.
        if (theme.config.motionReduced) {
          this.classList.add('is-lit');
          return;
        }

        const stop = Motion.inView(
          this,
          () => {
            this.classList.add('is-lit');
          },
          { amount: 0.5 }
        );

        this.registerCleanup(stop);
      }
    }
  );
}

/*
  The volume presets. They set the quantity on the card's product form and
  rewrite the displayed figure to the price of one can at that quantity.

  This used to set the quantity only, because the ladder the kit shipped with
  was invented and no discount behind it existed. One does now: the Rapi Bundle
  app's automatic discount takes 10% off ten cans and 12% off thirty, which is
  what the cart charges — measured against /cart.js, not taken from the app's
  admin. `snippets/snusify-bulk-tiers.liquid` holds those percentages and the
  method for re-checking them, and it renders the money strings this reads, so
  nothing here formats a currency or knows a percentage.
*/
if (!customElements.get('snusify-quantity')) {
  customElements.define(
    'snusify-quantity',
    class SnusifyQuantity extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'click', this.onClick.bind(this));
      }

      get input() {
        return this.closest('form')?.querySelector('[data-quantity-input]');
      }

      get card() {
        return this.closest('.snusify-pcard');
      }

      onClick(event) {
        const button = event.target.closest('[data-quantity-option]');
        if (!button || !this.contains(button)) return;

        for (const option of this.querySelectorAll('[data-quantity-option]')) {
          option.setAttribute('aria-pressed', String(option === button));
        }

        const input = this.input;
        if (input) input.value = button.getAttribute('data-quantity-option');

        this.showPrice(button);
      }

      // Every string here was rendered by Liquid's `money` filter on the button
      // itself, so the card stays correct in any currency and locale without a
      // money formatter in the theme's JS.
      showPrice(button) {
        const card = this.card;
        if (!card) return;

        const price = card.querySelector('[data-snusify-price]');
        const compare = card.querySelector('[data-snusify-compare]');

        const percan = button.getAttribute('data-percan-str');
        if (price && percan) price.textContent = percan;

        // Price then struck price — the same two-part shape a sale product
        // shows at rest, so a volume price reads as the same kind of thing.
        if (compare) {
          const was = button.getAttribute('data-was-str');
          compare.textContent = was || '';
          compare.hidden = !was;
        }
      }
    }
  );
}
